/**
 * Que proposer à quelqu'un qui vient d'atteindre une limite ?
 *
 * Trois questions, dans cet ordre :
 *   1. Quels paliers font sauter CETTE limite pour SON type de profil ?
 *      (`GET /profiletype_modules/unlocking/{profileTypeId}/{moduleCode}`)
 *   2. Détient-elle déjà un palier de ce module ?
 *   3. Si oui, c'est un changement de palier — pas un second achat.
 */

/**
 * Le code du tarif d'un abonnement, quelle que soit la casse rendue.
 */
function codeDeLAbonnement(abo) {
    const brut = abo?.subscriptionType ?? abo?.subscriptiontype ?? abo?.SUBSCRIPTIONTYPE
              ?? abo?.tarifCode ?? abo?.tarifcode ?? abo?.TARIFCODE ?? '';
    return String(brut).trim().toUpperCase();
}

/**
 * Décide entre acheter et changer de palier.
 *
 * Si la personne détient déjà l'un des paliers qui déplafonnent ce module,
 * lui vendre le suivant comme un nouvel achat lui ferait PAYER DEUX FOIS la
 * même app. `upgradeSubscription` existe pour cela, avec prorata et crédits.
 *
 * Aucune contrainte SQL ne doit forcer l'expiration : pendant un changement
 * de palier, les deux lignes coexistent brièvement.
 *
 * @param {Array} paliersQuiDeplafonnent - Tarifs rendus par /unlocking
 * @param {Array} abonnementsActifs - Abonnements en cours de la personne
 * @returns {object} { action, tarifPropose, tarifDetenu }
 *   action vaut 'acheter', 'upgrader' ou 'rien'
 */
export function deciderVente(paliersQuiDeplafonnent, abonnementsActifs) {
    const paliers = Array.isArray(paliersQuiDeplafonnent) ? paliersQuiDeplafonnent : [];
    const actifs = Array.isArray(abonnementsActifs) ? abonnementsActifs : [];

    if (paliers.length === 0) {
        // Aucun palier ne déplafonne ce module pour ce type de profil. Ne
        // rien proposer plutôt qu'un abonnement qui ne changerait rien.
        return { action: 'rien', tarifPropose: null, tarifDetenu: null };
    }

    const codesDetenus = new Set(actifs.map(codeDeLAbonnement).filter(Boolean));
    const estDetenu = (p) => codesDetenus.has(String(p?.code ?? '').toUpperCase());

    // Les paliers arrivent triés par prix croissant. On retient le PLUS ÉLEVÉ
    // qu'elle détient déjà — sans quoi, détenant FULL, on lui proposerait
    // STARTER comme « changement de palier » : un downgrade présenté comme
    // une solution à sa limite.
    let indexDetenu = -1;
    paliers.forEach((p, i) => {
        if (estDetenu(p)) indexDetenu = i;
    });

    // Et on ne propose que ce qui est AU-DESSUS de ce palier.
    const aProposer = paliers.slice(indexDetenu + 1).find(p => !estDetenu(p)) || null;

    if (indexDetenu === -1) {
        return { action: 'acheter', tarifPropose: aProposer, tarifDetenu: null };
    }

    if (!aProposer) {
        // Elle détient déjà le palier le plus élevé qui déplafonne ce module.
        // La limite vient d'ailleurs — ne rien vendre.
        return { action: 'rien', tarifPropose: null, tarifDetenu: paliers[indexDetenu] };
    }

    return { action: 'upgrader', tarifPropose: aProposer, tarifDetenu: paliers[indexDetenu] };
}

/**
 * Le texte du compteur : « 18 sur 20 », ou rien quand c'est illimité.
 *
 * GetLimit rend -1 pour « illimité », jamais NULL.
 *
 * @param {number} current
 * @param {number} max
 * @returns {string|null}
 */
export function libelleCompteur(current, max) {
    // Number(null) vaut 0 et Number('') aussi : un test de finitude seul
    // laisserait passer une absence de valeur pour un zéro légitime.
    if (current === null || current === undefined || current === '') return null;
    if (max === null || max === undefined || max === '') return null;

    const n = Number(current);
    const m = Number(max);

    if (!Number.isFinite(n) || !Number.isFinite(m)) return null;
    if (m < 0) return null; // illimité

    return `${n} / ${m}`;
}
